import "./column-chooser.css";

import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { Popup } from "@tql/ui-components-react/kendo/popup";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	columns_03Line,
	xCloseLine,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useRef, useState } from "react";

import type { BoardColumn } from "~/shared/types/quote-board-types.ts";

type ColumnChooserProps = {
	columns: BoardColumn[];
	onColumnToggle: (column: BoardColumn) => void;
	onResetColumns?: () => void;
	className?: string;
	secondary?: boolean;
};

export const ColumnChooser = ({
	columns,
	onColumnToggle,
	onResetColumns,
	className,
	secondary = false,
}: ColumnChooserProps) => {
	const [show, setShow] = useState(false);
	const anchorRef = useRef<HTMLDivElement>(null);

	const visibleCount = columns.filter((c) => c.show).length;

	const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setShow(true);
	};

	const handleClose = useCallback(() => {
		setShow(false);
	}, []);

	return (
		<div ref={anchorRef} className={className}>
			<Button
				aria-label={"Column chooser"}
				disabled={show}
				onClick={handleOpen}
				svgIcon={columns_03Line}
				title={"Column chooser"}
				variant={secondary ? "secondary" : "tertiary"}
			>
				{secondary ? "Column chooser" : null}
			</Button>
			<Popup
				anchor={anchorRef.current}
				anchorAlign={{ horizontal: "left", vertical: "bottom" }}
				animate={false}
				className={"column-chooser-popover"}
				onMouseDownOutside={handleClose}
				popupAlign={{ horizontal: "left", vertical: "top" }}
				show={show}
			>
				<div className={"column-chooser-header"}>
					<div className={"column-chooser-title"}>Column chooser</div>
					<Button
						aria-label={"Close column chooser"}
						onClick={handleClose}
						svgIcon={xCloseLine}
						variant={"tertiary"}
					/>
				</div>

				<div
					className={"column-chooser-content"}
					data-testid={"column-chooser"}
				>
					{columns
						.filter(
							(col) =>
								col.field !== "" &&
								col.title &&
								col.title.trim() !== "" &&
								col.menu !== false,
						)
						.map((col) => {
							const isLastVisible = col.show && visibleCount === 1;
							const isRequired = col.required === true;

							const isDisabled = isLastVisible || isRequired;

							return (
								<div
									key={col.field}
									aria-checked={col.show}
									aria-disabled={isDisabled}
									aria-label={col.title}
									className={`column-chooser-item${
										isDisabled ? " column-chooser-item-disabled" : ""
									}`}
									onClick={() => {
										if (!isDisabled) {
											onColumnToggle({
												...col,
												show: !col.show,
											});
										}
									}}
									onKeyDown={(e) => {
										if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
											e.preventDefault();
											onColumnToggle({
												...col,
												show: !col.show,
											});
										}
									}}
									role={"checkbox"}
									tabIndex={isDisabled ? -1 : 0}
								>
									<span
										aria-hidden={true}
										className={"column-chooser-item-visual"}
									>
										<Checkbox
											checked={col.show}
											disabled={isDisabled}
											id={`col-chooser-${col.field}`}
											label={col.title}
											onChange={() => {}}
											tabIndex={-1}
										/>
									</span>
								</div>
							);
						})}
				</div>
				{onResetColumns ? (
					<div className={"column-chooser-footer"}>
						<Button onClick={onResetColumns} variant={"tertiary"}>
							Reset to Default
						</Button>
					</div>
				) : null}
			</Popup>
		</div>
	);
};
